import type { FillMode } from '../../../video-data/types';
import { Config } from '../../../video-data/config';
import { calculateScaledResolution } from '../mediaUtils';

/**
 * How much of a media zoomed below a 1:1 fit must stay on screen when it is panned fully away.
 */
export const XY_TRANSLATION_MARGIN_PX = 10;

export type Size = { width: number; height: number };
export type Point = { x: number; y: number };
export type Rect = Point & Size;

/** The user's crop: a zoom, and a pan expressed as a fraction of the visible region. */
export type Crop = { scaleX: number; scaleY: number; tx: number; ty: number };

export type TranslationLimit = {
    /**
     *  txLimit - max value of transformMatrix.tx
     */
    x: [number, number];
    /**
     * tyLimit - max value of transformMatrix.ty
     */
    y: [number, number];
};

/**
 * Where the geometry reads its inputs from. Each is a function so a single long-lived instance
 * always sees current values, and so a getter only pays for what it actually reads — asking for
 * `displaySize` never touches the visible region.
 */
export type MediaGeometrySource = {
    /** The source's decoded resolution. */
    sourceResolution: () => Size;
    /** The box the source is scaled to fit. */
    fitBox: () => Size;
    /** The region the viewer actually sees the media in. */
    visibleRegion: () => Rect;
    fillMode: () => FillMode;
    crop: () => Crop;
    /** False once the user has placed the media themselves, or for a sticker. */
    isAutoCentered: () => boolean;
};

/**
 * How big the media is and where it sits, in scene pixels. The single answer to those questions, so
 * that the size a container is given, the limits offered to the editor and the region chosen for
 * rasterising cannot disagree with each other.
 *
 * Holds no PIXI and no DOM. One instance per asset, living as long as the asset does: it reads its
 * inputs on demand rather than being handed a snapshot, so there is nothing to keep in sync.
 */
export class MediaGeometry {
    #source: MediaGeometrySource;

    constructor(source: MediaGeometrySource) {
        this.#source = source;
    }

    /**
     * The size the media is drawn at: the source fitted to the fit box, times the zoom. Zoom applies
     * to cover only (LU-2036).
     */
    get displaySize(): Size {
        const fillMode = this.#source.fillMode();
        const fitted = calculateScaledResolution(
            this.#source.sourceResolution(),
            this.#source.fitBox(),
            fillMode,
        );
        const zoom = fillMode === Config.FILL_COVER ? this.#source.crop() : null;
        return {
            width: fitted.width * (zoom?.scaleX ?? 1),
            height: fitted.height * (zoom?.scaleY ?? 1),
        };
    }

    /**
     * Where the media's top-left corner lands, as the asset's container position. Reads the visible
     * region and the display size once and hands them to both halves: this runs per asset per frame,
     * and a stencil-masked media pays for a mask bounds calculation every time it is asked.
     */
    get originInVisibleRegion(): Point {
        const visibleRegion = this.#source.visibleRegion();
        const displaySize = this.displaySize;
        const centerOffset = this.#centerOffset(visibleRegion, displaySize);
        const cropTranslation = this.#cropTranslation(visibleRegion, displaySize);
        return {
            x: centerOffset.x - cropTranslation.x,
            y: centerOffset.y - cropTranslation.y,
        };
    }

    /**
     * How far the pan may go, as a fraction of the visible region, rounded for the editor. Derived
     * from the same clamp {@link originInVisibleRegion} applies, so a caller that respects this
     * limit is not silently re-clamped when the frame is drawn.
     */
    get translationLimit(): TranslationLimit {
        const visibleRegion = this.#source.visibleRegion();
        const crop = this.#source.crop();
        const displaySize = this.displaySize;
        const axis = (objectSize: number, visibleSize: number) => {
            const limit = MediaGeometry.#maxOffset(objectSize, visibleSize, crop) / visibleSize;
            return parseFloat(limit.toFixed(2));
        };
        const tx = axis(displaySize.width, visibleRegion.width);
        const ty = axis(displaySize.height, visibleRegion.height);
        return { x: [-tx, tx], y: [-ty, ty] };
    }

    /**
     * The zoom that makes the whole source visible inside the fit box: undo the fill mode's scale,
     * then apply a contain fit. Takes the declared source dimensions rather than the decoded ones,
     * which is the resolution this has always been computed from.
     */
    static scaleToContain(declaredResolution: Size, fitBox: Size, fillMode: FillMode): number {
        const { ratio } = calculateScaledResolution(declaredResolution, fitBox, fillMode);
        const containRatio = calculateScaledResolution(
            declaredResolution,
            fitBox,
            Config.FILL_CONTAIN,
        ).ratio;
        return (1 / ratio) * containRatio;
    }

    /**
     * The offset that centres the media in the visible region, or nothing when the user placed it.
     */
    #centerOffset(visibleRegion: Rect, displaySize: Size): Point {
        if (!this.#source.isAutoCentered()) {
            return { x: 0, y: 0 };
        }
        return {
            x: visibleRegion.x + visibleRegion.width / 2 - displaySize.width / 2,
            y: visibleRegion.y + visibleRegion.height / 2 - displaySize.height / 2,
        };
    }

    /**
     * How far the user's pan moves the media, clamped so it cannot be dragged further than it has
     * ground to give. A media zoomed below a 1:1 fit is allowed to leave the frame until only
     * {@link XY_TRANSLATION_MARGIN_PX} of it remains.
     */
    #cropTranslation(visibleRegion: Rect, displaySize: Size): Point {
        const crop = this.#source.crop();
        return {
            x: MediaGeometry.#axisTranslation(
                displaySize.width,
                visibleRegion.width,
                crop.tx,
                crop,
            ),
            y: MediaGeometry.#axisTranslation(
                displaySize.height,
                visibleRegion.height,
                crop.ty,
                crop,
            ),
        };
    }

    /**
     * How far off centre the media may sit on one axis. Half its overflow, plus — when it is zoomed
     * below a 1:1 fit and so has no overflow to give — almost its whole width. Shared, so the limit
     * offered to the editor and the clamp applied when the frame is drawn cannot drift apart.
     */
    static #maxOffset(objectSize: number, visibleSize: number, crop: Crop): number {
        const zoomIsLessThanOne = crop.scaleX < 1.0 || crop.scaleY < 1.0;
        const margin = zoomIsLessThanOne ? objectSize - XY_TRANSLATION_MARGIN_PX : 0;
        return Math.abs((objectSize - visibleSize) / 2) + margin;
    }

    static #axisTranslation(
        objectSize: number,
        visibleSize: number,
        translation: number,
        crop: Crop,
    ): number {
        const maxOffset = MediaGeometry.#maxOffset(objectSize, visibleSize, crop);
        const userOffset = Math.abs(translation * visibleSize);
        const offset = Math.min(maxOffset, userOffset);
        return offset * (translation < 0 ? -1 : 1);
    }
}
